import { reactive } from 'vue'
import type {
  DraftMap,
  OfflineMapServiceState,
  TransformedRegionToGeoJson,
} from '@/services/offline-map-service/types'
import { getDownloadSizeEstimate } from '@/services/offline-map-service/get-download-size-estimate'
import { isCustomName } from '@/services/offline-map-service/helpers'
import { toggleOfflineMapOverlay } from '@/services/offline-map-service/toggle-offline-map-overlay'
import { deleteMapEvent } from '@/services/offline-map-service/marketing-events'
import type { OFFLINE_MAPS_ORIGINS } from '@/lib/enums-marketing'
import { showLayer, hideLayer } from '@/services/map-service'
import { getOfflineMapService } from '@/services/offline-map-service/offline-map-service-instance'

export const offlineServiceState = reactive<OfflineMapServiceState>({
  hasRequested: false,
  serviceInitialized: false,
  offlineEventHandler: null,
  unwatchOverlay: () => {},
  draftMap: {},
  inCreateMode: false,
  map_area: null,
  maps: undefined,
  overlayFeature: [],
})

/**
 * Bridge helper: casts the package service to use local app types at the integration boundary.
 *
 * The package's types are structurally compatible at runtime but differ nominally —
 * for example, geometry.type is `string` in the package vs `GeoJsonGeometryTypes` locally,
 * and Position is a fixed-length `[number, number]` tuple vs the geojson variable-length array.
 * The CRUD methods that accept/return TransformedRegionToGeoJson or DraftMap are wrapped here
 * so TypeScript treats them as returning/accepting the local types throughout the rest of the file.
 */
const getLocalService = () =>
  getOfflineMapService() as unknown as {
    getOfflineMaps(): Promise<TransformedRegionToGeoJson[] | undefined>
    createOfflineMap(
      map: DraftMap
    ): Promise<TransformedRegionToGeoJson | undefined>
    updateOfflineMap(
      draftMap: TransformedRegionToGeoJson,
      existingMap: TransformedRegionToGeoJson
    ): Promise<TransformedRegionToGeoJson | undefined>
    deleteOfflineMap(map: TransformedRegionToGeoJson): Promise<void>
  }

const getOfflineMaps = (): Promise<TransformedRegionToGeoJson[] | undefined> =>
  getLocalService().getOfflineMaps()

const createOfflineMap = (
  map: DraftMap
): Promise<TransformedRegionToGeoJson | undefined> =>
  getLocalService().createOfflineMap(map)

const updateOfflineMap = (
  draftMap: TransformedRegionToGeoJson,
  existingMap: TransformedRegionToGeoJson
): Promise<TransformedRegionToGeoJson | undefined> =>
  getLocalService().updateOfflineMap(draftMap, existingMap)

const deleteOfflineMap = async (
  map: TransformedRegionToGeoJson,
  origin:
    | typeof OFFLINE_MAPS_ORIGINS.MAP_EDIT_CARD
    | typeof OFFLINE_MAPS_ORIGINS.MAP_INFO_CARD
    | typeof OFFLINE_MAPS_ORIGINS.MENU
) => {
  try {
    await getLocalService().deleteOfflineMap(map)
    deleteMapEvent(map, origin, offlineServiceState)
  } catch (e) {
    return undefined
  }
  return undefined
}

const initOfflineStateAndToggleLayers = () => {
  getOfflineMapService().initDraftMode()
  toggleOfflineMapOverlay()
}

const cleanupOfflineStateAndToggleLayers = () => {
  getOfflineMapService().cleanupDraftMode()
  toggleOfflineMapOverlay()
}

const updateOfflineStateDraftMapMaxArea = async () => {
  const mapDetail = offlineServiceState.draftMap.mapDetail
  if (!mapDetail) {
    return undefined
  }
  const zoomLevelsAndMaxArea =
    await getOfflineMapService().getZoomLevelsForDetailLevel(mapDetail)
  if (zoomLevelsAndMaxArea && zoomLevelsAndMaxArea.max_area) {
    offlineServiceState.draftMap.maxArea = zoomLevelsAndMaxArea.max_area
  }
  toggleOfflineMapOverlay()
}

const showOfflineMapsLayer = () => {
  showLayer({ id: 'offline-maps$', stylesheetID: 'offline-maps$' })
}

const hideOfflineMapsLayer = () =>
  hideLayer({ id: 'offline-maps$', stylesheetID: 'offline-maps$' })

export default {
  offlineServiceState,
  getDownloadSizeEstimate,
  isCustomName,
  getOfflineMaps,
  createOfflineMap,
  initOfflineStateAndToggleLayers,
  cleanupOfflineStateAndToggleLayers,
  updateOfflineMap,
  deleteOfflineMap,
  showOfflineMapsLayer,
  hideOfflineMapsLayer,
  updateOfflineStateDraftMapMaxArea,
}
